/* eslint-disable @typescript-eslint/no-explicit-any */
import { AgGridReact } from 'ag-grid-react'
import { CellValueChangedEvent, ColDef, ColGroupDef, ColumnMovedEvent, ColumnResizedEvent, DefaultMenuItem, GetContextMenuItemsParams, GridApi, GridOptions, IRowNode, MenuItemDef, SelectionChangedEvent } from 'ag-grid-community'
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { Button, Flex } from 'antd'
import { ClearOutlined, FileExcelOutlined, FullscreenExitOutlined, FullscreenOutlined, MinusSquareOutlined, PlusSquareOutlined, PushpinOutlined } from '@ant-design/icons'
import { useAgGridStatePersistence } from '@/hooks/useAgGridStatePersistence.ts'

interface CustomAgGridProps<T> {
  height?: number | string
  headerHeight?: number
  rowData?: T[] | null
  pinnedBottomRowData?: T[]
  columnDefs: ColDef[]
  gridOptions?: GridOptions
  refetch?: () => void
  isLoading?: boolean
  toolbarExtra?: React.ReactNode
  toolbarExtraOnLeft?: boolean
  allowExport?: boolean
  grandTotalRow?: 'top' | 'bottom'
  onCellValueChanged?: (event: CellValueChangedEvent<T>) => void
  onSelectionChanged?: (event: SelectionChangedEvent<T>) => void
  /** Unique ID to enable state persistence (columns, filters, sort) to backend */
  gridId?: string
  /** Called when the grid is ready, receives the GridApi */
  onGridReady?: (api: GridApi<T>) => void
  /** Called after first data render, receives the GridApi */
  onFirstDataRendered?: (api: GridApi<T>) => void
  /** Enable auto header height to wrap long header text */
  autoHeaderHeight?: boolean
  /** Column IDs to group by default (applied programmatically to avoid AG Grid declarative rowGroup bugs) */
  defaultRowGroupColumns?: string[]
}

export interface CustomAgGridRef<T> {
  getSelectedRows: () => T[]
  getAllRows: () => T[]
  getAllNodes: () => IRowNode<T>[]
  resetTableFilter: () => void
  /** Save the current layout to the backend (and localStorage). */
  saveLayout: () => Promise<void>
  /** Apply the code-default layout and keep it in localStorage. */
  restoreDefault: () => void
  /** Apply the backend-saved layout. Resolves false when there is none; rejects on request failure. */
  restoreSavedLayout: () => Promise<boolean>
  /** Expand every row group / tree node */
  expandAll: () => void
  /** Collapse every row group / tree node */
  collapseAll: () => void
  api: GridApi<T> | undefined
}

/** Cell class that marks a numeric / currency column. Also the excelStyles id below, so the
 *  on-screen number filter and the Excel number format are driven by the same marker. */
const NUMBER_CELL_CLASS = 'numberCell'

/** True when a column def carries the numeric marker.
 *
 *  The callback form of `cellClass` is deliberately treated as "not numeric": it needs a row to
 *  evaluate, and the filter has to be chosen once when the column is created. */
function isNumberCell(cellClass: ColDef['cellClass']): boolean {
  if (typeof cellClass === 'string') return cellClass === NUMBER_CELL_CLASS
  if (Array.isArray(cellClass)) return cellClass.includes(NUMBER_CELL_CLASS)
  return false
}

/** Stateful column-def attributes and their initial-only counterparts. See normalizeColumnDefs. */
const STATEFUL_TO_INITIAL_ATTRS = [
  ['hide', 'initialHide'],
  ['width', 'initialWidth'],
  ['flex', 'initialFlex'],
  ['pinned', 'initialPinned'],
  ['sort', 'initialSort'],
  ['sortIndex', 'initialSortIndex'],
  ['aggFunc', 'initialAggFunc'],
] as const satisfies readonly (readonly [keyof ColDef, keyof ColDef])[]

/** Recursively normalise column definitions: stateful attributes (`hide`, `width`, `flex`,
 *  `pinned`, `sort`, `sortIndex`, `aggFunc`) become their `initial*` counterparts, and numeric
 *  columns get the number filter.
 *
 *  On the filter: `filter: true` in defaultColDef resolves to the *set* filter for every column,
 *  because SetFilterModule is registered and suppressSetFilterByDefault is not set. A set filter
 *  on an amount column lists every distinct value, which is close to useless when the values are
 *  free-form money. Columns marked with NUMBER_CELL_CLASS therefore get agNumberColumnFilter
 *  instead, with its greater-than / less-than / in-range operators.
 *
 *  A column that names its own `filter` is left alone, so a page can still opt out.
 *
 *  Number-filter columns inherit `filterParams` from defaultColDef, where the only entry is the
 *  set filter's `excelMode`. The number filter ignores params it does not know.
 *
 *  AG Grid applies `initial*` attributes once, when the column is created, and when
 *  resetColumnState() runs. They are deliberately ignored when columnDefs are
 *  re-supplied. Stateful attributes, by contrast, are re-applied on every columnDefs
 *  update, so a page that rebuilds its defs (e.g. the deferrals grid when the snapshot
 *  month changes) would undo the user's restored layout: widths back to flex, aggregation
 *  back to the default, columns re-shown. Converting them keeps "Restore Default Layout"
 *  (resetColumnState) working while leaving the user's layout alone on re-renders.
 *
 *  Expressing the default declaratively also means the grid needs no
 *  applyColumnState() call on grid ready. That call used to run before the grid
 *  had been laid out, so it recalculated flex widths against a container size
 *  that was not final yet. */
function normalizeColumnDefs<T extends ColDef | ColGroupDef>(defs: T[]): T[] {
  return defs.map(def => {
    if ('children' in def && def.children) {
      return { ...def, children: normalizeColumnDefs(def.children) }
    }

    const colDef = def as ColDef
    // Left null while nothing needs changing, so unmodified defs keep their original reference.
    let next: ColDef | null = null

    for (const [statefulKey, initialKey] of STATEFUL_TO_INITIAL_ATTRS) {
      if (colDef[statefulKey] === undefined) continue
      const copy: Record<string, unknown> = { ...(next ?? colDef) }
      copy[initialKey] = colDef[statefulKey]
      delete copy[statefulKey]
      next = copy as ColDef
    }

    if (colDef.filter === undefined && isNumberCell(colDef.cellClass)) {
      next = { ...(next ?? colDef), filter: 'agNumberColumnFilter' }
    }

    return (next ?? def) as T
  })
}

  export const CustomAgGrid = forwardRef(
  <T, >({
    height = 625,
    rowData,
    pinnedBottomRowData,
    columnDefs,
    gridOptions,
    refetch,
    isLoading = false,
    toolbarExtra,
    toolbarExtraOnLeft = false,
    allowExport = true,
    grandTotalRow,
    onCellValueChanged,
    onSelectionChanged,
    gridId,
    onGridReady: onGridReadyProp,
    onFirstDataRendered: onFirstDataRenderedProp,
    autoHeaderHeight = false,
    defaultRowGroupColumns,
  }: CustomAgGridProps<T>, ref: any) => {
    const gridRef = useRef<AgGridReact<T>>(null)
    const [isFullscreen, setIsFullscreen] = useState(false)
    const [userPinnedTopRows, setUserPinnedTopRows] = useState<T[]>([])
    /** True when the grid currently has expandable rows (row grouping or tree data). */
    const [hasExpandableRows, setHasExpandableRows] = useState(false)
    /** True when no expandable row is left collapsed, so the button offers "Collapse All". */
    const [allExpanded, setAllExpanded] = useState(false)

    // Turn stateful attributes into `initial*` ones so that AG Grid cannot re-apply them
    // over the user's layout on re-renders. Memoized to keep a stable reference —
    // AG Grid treats a changed columnDefs reference as a full column reset,
    // which queues internal rAF layout callbacks. If those fire after the
    // component unmounts (e.g. page navigation), they crash with a null
    // columnModel ("getProvidedColumnGroup" error).
    const effectiveColumnDefs = useMemo(() => normalizeColumnDefs(columnDefs), [columnDefs])

    const defaultRowGroupColumnsRef = useRef(defaultRowGroupColumns)
    useEffect(() => { defaultRowGroupColumnsRef.current = defaultRowGroupColumns }, [defaultRowGroupColumns])

    // The part of the code-default layout that column defs cannot express. Grouping is applied
    // programmatically rather than with `rowGroup` on the defs to avoid AG Grid's declarative
    // rowGroup bugs. resetColumnState() has already cleared any user grouping before this runs.
    const applyDefaultLayout = useCallback((api: GridApi) => {
      const groups = defaultRowGroupColumnsRef.current
      if (groups?.length) api.setRowGroupColumns(groups)
    }, [])

    // Layout sources: first load → localStorage, "Restore Saved Layout" → backend,
    // "Restore Default Layout" → code default. See useAgGridStatePersistence.
    const {
      onGridReady: onGridReadyPersistence,
      onFirstDataRendered: onFirstDataRenderedPersistence,
      persistState,
      saveState: saveLayout,
      restoreDefault,
      restoreSavedLayout,
    } = useAgGridStatePersistence(gridId, { applyDefaultLayout })

    /** Column drags and resizes fire continuously; only the final position / width is persisted.
     *  Flex re-layouts (container resize) are not user changes and are skipped too. */
    const persistColumnMoved = useCallback((e: ColumnMovedEvent) => {
      if (e.finished) persistState()
    }, [persistState])
    const persistColumnResized = useCallback((e: ColumnResizedEvent) => {
      if (e.finished && e.source !== 'flex') persistState()
    }, [persistState])

    const resetTableFilter = () => {
      gridRef.current?.api.setFilterModel(null)
      gridRef.current?.api.onFilterChanged()
    }

    /** Recompute whether the expand/collapse-all button should show, and which action it offers.
     *
     *  Derived from the actual node state rather than tracked locally, so manually
     *  collapsing a single group flips the button back to "Expand All". */
    const isTreeData = !!gridOptions?.treeData
    const syncExpandState = useCallback(() => {
      const api = gridRef.current?.api
      if (!api || api.isDestroyed()) return

      const expandable = isTreeData || api.getRowGroupColumns().length > 0
      setHasExpandableRows(expandable)
      if (!expandable) {
        setAllExpanded(false)
        return
      }

      // forEachNode walks every node in the model, including children of collapsed groups.
      let anyCollapsed = false
      api.forEachNode(node => {
        if (anyCollapsed) return
        // `group` covers row-grouped nodes; childrenAfterGroup covers tree-data parents.
        const isParent = node.group || (node.childrenAfterGroup?.length ?? 0) > 0
        if (isParent && !node.expanded) anyCollapsed = true
      })
      setAllExpanded(!anyCollapsed)
    }, [isTreeData])

    const expandAll = useCallback(() => {
      const api = gridRef.current?.api
      if (!api || api.isDestroyed()) return
      api.expandAll()
    }, [])

    const collapseAll = useCallback(() => {
      const api = gridRef.current?.api
      if (!api || api.isDestroyed()) return
      api.collapseAll()
    }, [])

    useImperativeHandle(ref, () => ({
      getSelectedRows: () => {
        if (gridRef.current) {
          return gridRef.current.api.getSelectedRows()
        }
        return []
      },
      getAllRows: () => {
        if (gridRef.current) {
          const rowData: T[] = []
          gridRef.current.api.forEachNode((node) => {
            if (node.data) {
              rowData.push(node.data)
            }
          })
          return rowData
        }
        return []
      },
      getAllNodes: () => {
        if (gridRef.current) {
          const nodes: IRowNode<T>[] = []
          gridRef.current.api.forEachNode((node) => {
            if (node.data) {
              nodes.push(node)
            }
          })
          return nodes
        }
        return []
      },
      resetTableFilter: () => {
        if (gridRef.current) {
          resetTableFilter()
        }
      },
      saveLayout,
      restoreDefault,
      restoreSavedLayout,
      expandAll,
      collapseAll,
      api: gridRef.current?.api,
    }))

    /** Rows the user has explicitly pinned to the top, filtered from normal rowData. */
    const pinnedTopRowSet = useMemo(() => new Set(userPinnedTopRows), [userPinnedTopRows])

    const filteredRowData = useMemo(() => {
      if (!rowData || userPinnedTopRows.length === 0) return rowData
      return rowData.filter(row => !pinnedTopRowSet.has(row))
    }, [rowData, userPinnedTopRows, pinnedTopRowSet])

    // Use a ref so the stable getContextMenuItems callback always sees latest state
    // without causing mergedGridOptions to be recreated on every pin/unpin.
    const pinnedTopRowSetRef = useRef(pinnedTopRowSet)
    useEffect(() => { pinnedTopRowSetRef.current = pinnedTopRowSet }, [pinnedTopRowSet])
    const setUserPinnedTopRowsRef = useRef(setUserPinnedTopRows)
    const isTreeDataRef = useRef(isTreeData)
    useEffect(() => { isTreeDataRef.current = isTreeData }, [isTreeData])

    const getContextMenuItems = useCallback((params: GetContextMenuItemsParams<T>): (DefaultMenuItem | MenuItemDef)[] => {
      const data = params.node?.data
      const isPinnedRow = !!params.node?.rowPinned
      const isGroupRow = !!params.node?.group
      const set = pinnedTopRowSetRef.current
      const setter = setUserPinnedTopRowsRef.current

      const pinItems: (DefaultMenuItem | MenuItemDef)[] = []

      if (isGroupRow && params.node) {
        // Collect all leaf-level data under this group
        const leafData: T[] = (params.node.allLeafChildren ?? [])
          .map(n => n.data)
          .filter((d): d is T => d != null)

        if (leafData.length > 0) {
          const allPinned = leafData.every(d => set.has(d))
          const anyPinned = leafData.some(d => set.has(d))

          if (allPinned) {
            pinItems.push({
              name: 'Unpin Group',
              icon: '<span style="font-size:14px">📌</span>',
              action: () => setter(prev => prev.filter(r => !leafData.includes(r))),
            })
          } else {
            pinItems.push({
              name: anyPinned ? 'Pin Remaining Group Rows' : 'Pin Group to Top',
              icon: '<span style="font-size:14px">📌</span>',
              action: () => setter(prev => {
                const existing = new Set(prev)
                const toAdd = leafData.filter(d => !existing.has(d))
                return [...prev, ...toAdd]
              }),
            })
            if (anyPinned) {
              pinItems.push({
                name: 'Unpin Group',
                icon: '<span style="font-size:14px">📌</span>',
                action: () => setter(prev => prev.filter(r => !leafData.includes(r))),
              })
            }
          }
        }
      } else if (data) {
        if (isPinnedRow && params.node?.rowPinned === 'top') {
          // Row is in the pinned section — always offer unpin
          pinItems.push({
            name: 'Unpin Row',
            icon: '<span style="font-size:14px">📌</span>',
            action: () => setter(prev => prev.filter(r => r !== data)),
          })
        } else if (!isPinnedRow) {
          if (set.has(data)) {
            pinItems.push({
              name: 'Unpin Row',
              icon: '<span style="font-size:14px">📌</span>',
              action: () => setter(prev => prev.filter(r => r !== data)),
            })
          } else {
            pinItems.push({
              name: 'Pin Row to Top',
              icon: '<span style="font-size:14px">📌</span>',
              action: () => setter(prev => [...prev, data]),
            })
          }
        }
      }

      // AG Grid's own expandAll/contractAll items are only meaningful when the grid
      // has expandable rows, so they are added conditionally — this component replaces
      // the default menu entirely, which is why they have to be re-added by hand.
      const canExpand = isTreeDataRef.current || params.api.getRowGroupColumns().length > 0
      const expandItems: (DefaultMenuItem | MenuItemDef)[] = canExpand
        ? ['expandAll', 'contractAll', 'separator']
        : []

      const defaultItems: (DefaultMenuItem | MenuItemDef)[] = [...expandItems, 'copy', 'copyWithHeaders', 'separator', 'export']
      return pinItems.length > 0 ? [...pinItems, 'separator', ...defaultItems] : defaultItems
    }, []) // stable — reads from refs

    const mergedGridOptions = useMemo<GridOptions>(() => {return {
        enableBrowserTooltips: true,
        defaultColDef: {
          sortable: true,
          filter: true,
          resizable: true,
          // initialFlex, not flex: defaultColDef is merged into every column def, and a stateful
          // `flex` would be re-applied whenever columnDefs change, wiping restored pixel widths.
          initialFlex: 1,
          minWidth: 150,
          enableRowGroup: true,
          enablePivot: true,
          enableValue: true,
          // Windows Excel Mode gives the set filter its "Add current selection to filter" option,
          // so users can combine several searches into one filter. applyMiniFilterWhileTyping is
          // deliberately not set: it applies the filter on every keystroke, which replaces the
          // applied selection before the user can tick that option.
          filterParams: { excelMode: 'windows' },
          ...(autoHeaderHeight ? { wrapHeaderText: true, autoHeaderHeight: true } : {}),
        },
        pivotMode: false,
        // Keep the user's column order when a page rebuilds its columnDefs; by default AG Grid
        // resets the order to the definition order on every update.
        maintainColumnOrder: true,
        sideBar: {
          toolPanels: ['filters', 'columns'],
        },
        pagination: true,
        paginationPageSize: 20,
        paginationPageSizeSelector: [20, 50, 100],
        suppressContextMenu: false,
        // Excel ends copied cells with a line break, which AG Grid otherwise reads as an extra
        // blank row and pastes into the row below — clearing it.
        suppressLastEmptyLineOnPaste: true,
        rowSelection: {
          mode: 'multiRow',
          groupSelects: 'filteredDescendants',
          selectAll: 'filtered',
          headerCheckbox: true,
        },
        statusBar: {
          statusPanels: [
            { statusPanel: 'agTotalRowCountComponent' },
            { statusPanel: 'agFilteredRowCountComponent' },
            { statusPanel: 'agSelectedRowCountComponent' },
          ],
        },
        ...gridOptions,
        // Always override getContextMenuItems so the pin feature takes precedence
        getContextMenuItems,
        excelStyles: [
          {
            id: 'dateISO',
            dataType: 'DateTime',
            numberFormat: {
              format: 'yyy-mm-ddThh:mm:ss',
            },
          },
          {
            // Standard numeric/currency columns: comma-separated, (brackets) for negatives, blue zero
            id: NUMBER_CELL_CLASS,
            dataType: 'Number',
            numberFormat: {
              format: '#,##0.00;[Red](#,##0.00);[Blue]0.00',
            },
          },
        ],
      }
    }, [gridOptions, autoHeaderHeight, getContextMenuItems])

    const actionButtons = (
      <Flex gap="small">
        {refetch ? <Button onClick={refetch} loading={isLoading}>Refresh</Button> : null}
        <Button
          onClick={resetTableFilter}
          htmlType="button"
          icon={<ClearOutlined />}
        >
          Reset Filter
        </Button>
        {hasExpandableRows ? (
          <Button
            onClick={() => (allExpanded ? collapseAll() : expandAll())}
            htmlType="button"
            icon={allExpanded ? <MinusSquareOutlined /> : <PlusSquareOutlined />}
          >
            {allExpanded ? 'Collapse All' : 'Expand All'}
          </Button>
        ) : null}
        {userPinnedTopRows.length > 0 ? (
          <Button
            onClick={() => setUserPinnedTopRows([])}
            htmlType="button"
            icon={<PushpinOutlined />}
          >
            Clear Pinned ({userPinnedTopRows.length})
          </Button>
        ) : null}
        {allowExport ? (
            <Button
              onClick={() => gridRef.current?.api.exportDataAsExcel()}
              htmlType="button"
              icon={<FileExcelOutlined />}
            >
              Export
            </Button>
          )
          : null
        }
        <Button
          onClick={() => setIsFullscreen(f => !f)}
          htmlType="button"
          icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
        >
          {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
        </Button>
      </Flex>
    )

    const fullscreenStyle: React.CSSProperties = isFullscreen
      ? { position: 'fixed', inset: 0, zIndex: 1000, background: 'white', padding: '12px', boxSizing: 'border-box' }
      : {}

    return (
      <Flex
        vertical
        gap="small"
        className="ag-theme-quartz"
        style={{ height: isFullscreen ? '100%' : height, width: '100%', ...fullscreenStyle }}
      >
        <Flex justify="space-between">
          {toolbarExtraOnLeft ? toolbarExtra : actionButtons}
          {toolbarExtraOnLeft ? actionButtons : toolbarExtra}
        </Flex>
        <AgGridReact
          ref={gridRef}
          rowData={filteredRowData}
          pinnedTopRowData={userPinnedTopRows}
          pinnedBottomRowData={pinnedBottomRowData}
          columnDefs={effectiveColumnDefs}
          loading={isLoading}
          gridOptions={mergedGridOptions}
          grandTotalRow={grandTotalRow}
          onCellValueChanged={onCellValueChanged as any}
          onSelectionChanged={onSelectionChanged as any}
          onGridReady={(e) => {
            // Default visibility etc. come from the `initial*` attributes in effectiveColumnDefs;
            // the hook applies the localStorage layout here, if there is one.
            onGridReadyPersistence(e.api)
            onGridReadyProp?.(e.api)
          }}
          onFirstDataRendered={(e) => {
            // Applies the restored filters (set filters need row data). Default row grouping is
            // applied by the persistence hook when no stored layout exists.
            onFirstDataRenderedPersistence(e.api)
            onFirstDataRenderedProp?.(e.api)
            syncExpandState()
          }}
          onRowGroupOpened={syncExpandState}
          onExpandOrCollapseAll={syncExpandState}
          onRowDataUpdated={syncExpandState}
          onColumnMoved={persistColumnMoved}
          onColumnResized={persistColumnResized}
          onColumnVisible={persistState}
          onColumnPinned={persistState}
          onSortChanged={persistState}
          onFilterChanged={persistState}
          onColumnRowGroupChanged={() => { persistState(); syncExpandState() }}
          onColumnPivotChanged={persistState}
          onColumnValueChanged={persistState}
        />
      </Flex>
    )
  }) as unknown as <T>(props: {
    height?: number | string
    headerHeight?: number
    rowData?: T[] | null
    pinnedBottomRowData?: T[]
    columnDefs: (ColDef | ColGroupDef)[]
    gridOptions?: GridOptions
    refetch?: () => void
    isLoading?: boolean
    toolbarExtra?: React.ReactNode
    toolbarExtraOnLeft?: boolean
    allowExport?: boolean
    grandTotalRow?: 'top' | 'bottom'
    onCellValueChanged?: (event: CellValueChangedEvent<T>) => void
    onSelectionChanged?: (event: SelectionChangedEvent<T>) => void
    gridId?: string
    onGridReady?: (api: GridApi<T>) => void
    onFirstDataRendered?: (api: GridApi<T>) => void
    autoHeaderHeight?: boolean
    defaultRowGroupColumns?: string[]
    ref?: React.Ref<CustomAgGridRef<T>>
  }) => React.ReactNode
